
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MessageCircle, X, CheckCircle, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useExitIntent } from "@/hooks/useExitIntent";
import { isAndroidTWA } from "@/utils/runtimeContext";
import { useAnalytics } from "@/hooks/useAnalytics";

const PixPromotionModal = () => {
  const { user } = useAuth();
  const { track } = useAnalytics();
  const [showModal, setShowModal] = useState(false);
  const { showExitIntent, resetExitIntent } = useExitIntent();
  if (isAndroidTWA()) return null;

  useEffect(() => {
    console.log('PixPromotionModal - Exit intent detectado:', showExitIntent);
    
    // Verificar se deve mostrar o modal
    const shouldShowModal = () => {
      // Mostrar para usuários não logados (visitantes)
      if (!user) {
        console.log('Mostrando modal: usuário não logado (visitante)');
        return true;
      }
      
      console.log('User plan:', user.plan);
      console.log('User isSubscribed:', user.isSubscribed);
      
      // Não mostrar para usuários assinantes
      if (user.isSubscribed) {
        console.log('Não mostrando modal: usuário é assinante');
        return false;
      }
      
      // Mostrar para usuários logados sem assinatura (gratuitos ou em teste)
      if (user.plan === 'free' || !user.plan) {
        console.log('Mostrando modal: usuário gratuito ou sem plano');
        return true;
      }
      
      console.log('Mostrando modal: outros casos (usuários em teste)');
      return true; // Mostrar para qualquer usuário logado que não seja assinante
    };

    // Verificar se já foi mostrado nesta sessão
    const hasShownInSession = sessionStorage.getItem('pixPromotionShown');
    console.log('hasShownInSession:', hasShownInSession);
    
    // Só mostrar se exit intent foi detectado, deve mostrar modal e não foi mostrado na sessão
    if (showExitIntent && shouldShowModal() && !hasShownInSession) {
      console.log('Exibindo modal PIX por exit intent');
      setShowModal(true);
      sessionStorage.setItem('pixPromotionShown', 'true');
      resetExitIntent(); // Reset para evitar múltiplas ativações
    }
  }, [showExitIntent, user, resetExitIntent]);

  const handleWhatsAppClick = () => {
    track("whatsapp_click", { origin: "exit_intent_modal" });
    const message = encodeURIComponent(
      "Olá! Vim do site IA Palpite Guru e gostaria de tirar uma dúvida / me tornar assinante via PIX. Podem me ajudar?"
    );
    window.open(`https://wa.me/5573981988263?text=${message}`, "_blank");
    setShowModal(false);
  };

  const handleClose = () => {
    track("whatsapp_click" as any, { origin: "exit_intent_modal", action: "closed_x" });
    setShowModal(false);
  };

  const handleKeepBrowsing = () => {
    track("whatsapp_click" as any, { origin: "exit_intent_modal", action: "keep_browsing" });
    setShowModal(false);
  };

  console.log('PixPromotionModal - render, showModal:', showModal);

  if (!showModal) return null;

  return (
    <Dialog open={showModal} onOpenChange={setShowModal}>
      <DialogContent 
        className="max-w-sm sm:max-w-md mx-auto p-0 overflow-hidden w-[85%] sm:w-[90%] max-h-[75%] sm:max-h-[80%] overflow-y-auto"
        onInteractOutside={() => setShowModal(false)}
      >
        {/* Header obrigatório para acessibilidade do Dialog */}
        <DialogHeader className="sr-only">
          <DialogTitle>Promoção PIX</DialogTitle>
          <DialogDescription>Assine via PIX pelo WhatsApp</DialogDescription>
        </DialogHeader>
        <div className="animate-fade-in">
          <Card className="border-0 shadow-2xl">
            <CardContent className="p-0">
              {/* Header com gradiente */}
              <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-indigo-600 p-4 pr-14 text-white relative overflow-hidden">
                <button
                  onClick={handleClose}
                  aria-label="Fechar"
                  className="absolute top-3 right-3 h-10 w-10 flex items-center justify-center rounded-full bg-white text-zinc-900 shadow-lg ring-2 ring-white/70 hover:bg-zinc-100 hover:scale-105 active:scale-95 transition z-20"
                >
                  <X className="h-5 w-5" strokeWidth={2.5} />
                </button>

                {/* Elementos decorativos */}
                <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-full -translate-y-8 translate-x-8"></div>
                <div className="absolute bottom-0 left-0 w-12 h-12 bg-white/10 rounded-full translate-y-6 -translate-x-6"></div>

                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-5 w-5 text-yellow-300 flex-shrink-0" />
                    <h2 className="text-base sm:text-lg font-bold leading-tight">
                      Precisa de ajuda para começar?
                    </h2>
                  </div>
                  <p className="text-sm opacity-90">
                    Fale com a gente no WhatsApp — sem compromisso
                  </p>
                </div>
              </div>

              {/* Conteúdo principal */}
              <div className="p-4">
                <div className="text-center mb-4">
                  <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                    <strong>Antes de sair, que tal garantir seu acesso completo via PIX?</strong> Sem cartão de crédito!
                  </p>
                  
                  <p className="text-xs text-gray-600 mb-4">
                    Tenha acesso imediato a todos os palpites premium: <strong>Milhar, Centena, Duques, Ternos, Grupos, Lotofácil, Mega-Sena, Quina</strong> e muito mais!
                  </p>
                </div>

                {/* Benefícios em destaque */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                    <span className="font-semibold text-green-800 text-sm">✅ Liberação imediata após o pagamento!</span>
                  </div>
                  
                  <div className="space-y-1 text-xs text-green-700">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                      <span>Acesso completo a todos os palpites premium</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                      <span>Sem necessidade de cartão de crédito</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                      <span>Ativação instantânea via WhatsApp</span>
                    </div>
                  </div>
                </div>

                <div className="text-center mb-3">
                  <p className="text-xs text-gray-600 mb-3">
                    Clique no botão abaixo para falar conosco no WhatsApp e garantir seu acesso.
                  </p>
                </div>

                {/* Botão WhatsApp em destaque */}
                <Button
                  onClick={handleWhatsAppClick}
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 text-sm shadow-lg hover:shadow-green-500/25 transition-all duration-300"
                >
                  <MessageCircle className="h-4 w-4" />
                  Falar no WhatsApp agora
                </Button>

                <Button
                  onClick={handleKeepBrowsing}
                  variant="ghost"
                  className="w-full mt-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 text-sm"
                >
                  Continuar navegando
                </Button>

                <p className="text-xs text-gray-500 mt-2 text-center">
                  Respondemos rapidamente e sem compromisso.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PixPromotionModal;
